// produktpartnerService.ts — Gesellschaft-Stammdaten (global, GDV) und das
// Ansprechpartner-Profil am Kontakt.
//
// Der Produktpartner selbst ist ein normaler Firmen-Kontakt und wird über die
// üblichen Kontakt-Endpunkte geladen/gespeichert; hier liegt nur, was es
// ausschließlich in dieser Rolle gibt.

import { apiFetch } from "../utils/apiFetch";

/** Listen-Projektion aus `GET /gesellschaften`. */
export type GesellschaftListItem = {
  id: number;
  typ: string;
  name: string;
  gdvId?: number | null;
  ort?: string | null;
  website?: string | null;
  hasLogo: boolean;
};

/** Volldatensatz aus `GET /gesellschaften/:id` — trägt die Vorbefüllung. */
export type GesellschaftDetail = GesellschaftListItem & {
  strasse?: string | null;
  plz?: string | null;
  land?: string | null;
  telefon?: string | null;
  fax?: string | null;
  email?: string | null;
};

export type PartnerProfile = {
  contactId: number;
  partnerContactId?: number | null;
  standort?: string | null;
  organisationseinheit?: string | null;
  position?: string | null;
  positionVon?: string | null;
  positionSonstiges?: string | null;
  positionFreitext?: string | null;
  funktionsbereich?: string | null;
  funktionsbereichSonstiges?: string | null;
  sparten?: string[] | null;
  spartenSpezialkonzepte?: string | null;
  spartenSonstiges?: string | null;
  eintrittsdatum?: string | null;
  hobbys?: string | null;
  vorlieben?: string | null;
  bemerkungen?: string | null;
};

/** Ein Ansprechpartner, wie ihn die Produktpartner-Detailseite auflistet. */
export type PartnerAnsprechpartner = {
  contactId: number;
  name: string;
  firstName?: string | null;
  callName?: string | null;
  position?: string | null;
  positionFreitext?: string | null;
  funktionsbereich?: string | null;
  standort?: string | null;
  organisationseinheit?: string | null;
  sparten?: string[] | null;
  photo_url?: string | null;
};

/** Produktpartner des Mandanten (Auswahlliste beim Ansprechpartner). */
export type ProduktpartnerOption = {
  id: number;
  name: string;
  kurzname?: string | null;
  gesellschaftId?: number | null;
};

export async function listGesellschaften(
  signal?: AbortSignal,
): Promise<GesellschaftListItem[]> {
  const { data } = await apiFetch<GesellschaftListItem[]>("gesellschaften", { signal });
  return Array.isArray(data) ? data : [];
}

export async function getGesellschaft(
  id: number,
  signal?: AbortSignal,
): Promise<GesellschaftDetail | null> {
  try {
    const { data } = await apiFetch<GesellschaftDetail>(`gesellschaften/${id}`, { signal });
    return data ?? null;
  } catch {
    return null;
  }
}

export async function listProduktpartner(
  signal?: AbortSignal,
  art: "Produktpartner" | "Kooperationspartner" = "Produktpartner",
): Promise<ProduktpartnerOption[]> {
  const { data } = await apiFetch<ProduktpartnerOption[]>(
    `contact-partner-profile/produktpartner?art=${encodeURIComponent(art)}`,
    { signal },
  );
  return Array.isArray(data) ? data : [];
}

export async function getPartnerProfile(
  contactId: number,
  signal?: AbortSignal,
): Promise<PartnerProfile | null> {
  const { data } = await apiFetch<PartnerProfile | null>(
    `contact-partner-profile/${contactId}`,
    { signal },
  );
  return data ?? null;
}

/** Upsert — Content-Type ist Pflicht, sonst kommt ein leerer Body an. */
export async function savePartnerProfile(
  contactId: number,
  profile: Partial<PartnerProfile>,
): Promise<void> {
  await apiFetch(`contact-partner-profile/${contactId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profile),
  });
}

export async function listPartnerAnsprechpartner(
  partnerContactId: number,
  signal?: AbortSignal,
): Promise<PartnerAnsprechpartner[]> {
  const { data } = await apiFetch<PartnerAnsprechpartner[]>(
    `contact-partner-profile/by-partner/${partnerContactId}`,
    { signal },
  );
  return Array.isArray(data) ? data : [];
}

export async function assignPartnerProfile(contactId: number, profile: Partial<PartnerProfile>): Promise<void> {
  await apiFetch(`contact-partner-profile/${contactId}/assign`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(profile),
  });
}
